/* import __COLOCATED_TEMPLATE__ from './joined-list.hbs.js'; */
var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _class, _class2, _descriptor, _class3;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import { tagName } from '@ember-decorators/component';
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';

/**
## Joined List

Joins a list of strings with proper connectors (e.g. ", "; " and "; ", and").
If used as a standalone component, renders the complete string with connectors.
If used in block form, yields `value` and the block is used to render each list element. The connectors are included automatically.

### Example

```example
@title Basic usage

@controller-properties
{
  values: ['A', 'B', 'C']
}

@hbs
{{joined-list values}}
```

### Advanced Usage

```example
@title Advanced Usage

@controller-properties
{
  values: ['A', 'B', 'C']
}

@hbs
{{#joined-list
  values
  wordsConnector=" COMMA "
  lastWordConnector=" COMMA AND " as |value|~}}
  <span class='type-color-blue-medium'>{{value}}</span>
{{~/joined-list}}
```

### Parameters
@param {Array<String>} [values] first positional param
@param {String} [wordsConnector] connector to use between non-final elements for a list with length > 2. defaults to t('common.toSentence.wordsConnector')
@param {String} [twoWordsConnector] connector to use for a list of length === 2. defaults to t('common.toSentence.twoWordsConnector')
@param {String} [lastWordConnector] connector to use between the final two elements for a list with length > 2. defaults to t('common.toSentence.lastWordConnector')
@param {Number} [maxWordsToShow] maximum number of words to show. If there are N more words than maxWordsToShow, combine them into a single value of "N more"
*/
let JoinedList = (_dec = tagName('span'), _dec2 = computed('wordsConnector', 'i18n'), _dec3 = computed('lastWordConnector', 'i18n'), _dec4 = computed('twoWordsConnector', 'i18n'), _dec5 = computed('_lastWordConnector', '_twoWordsConnector', '_wordsConnector', 'truncatedValues'), _dec6 = computed('values.[]', 'maxWordsToShow', 'i18n'), _dec(_class = (_class2 = (_class3 = class JoinedList extends Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, 'data-test-joined-list', true);
    _initializerDefineProperty(this, "i18n", _descriptor, this);
  }
  get _wordsConnector() {
    if (this.wordsConnector) {
      return this.wordsConnector;
    }
    return this.i18n.t('common.toSentence.wordsConnector');
  }
  get _lastWordConnector() {
    if (this.lastWordConnector) {
      return this.lastWordConnector;
    }
    return this.i18n.t('common.toSentence.lastWordConnector');
  }
  get _twoWordsConnector() {
    if (this.twoWordsConnector) {
      return this.twoWordsConnector;
    }
    return this.i18n.t('common.toSentence.twoWordsConnector');
  }

  /**
   * @param {Number} index the current index
   * @param {Number} length the total number of items in the list
   * @returns {String} the connector that _precedes_ the given index
   */
  connector(index, length) {
    // return early for the trivial cases of the first element or a single-element list
    if (index === 0) {
      return '';
    }
    if (index === length - 1) {
      if (length === 2) {
        return this._twoWordsConnector;
      } else {
        return this._lastWordConnector;
      }
    } else {
      return this._wordsConnector;
    }
  }
  get parts() {
    const wordsToShow = this.truncatedValues;
    const length = wordsToShow.length;
    return wordsToShow.map((value, index) => {
      return {
        connector: this.connector(index, length),
        value
      };
    });
  }
  get truncatedValues() {
    const length = this.get('values.length'); // eslint-disable-line ember/no-get
    const max = this.maxWordsToShow;
    if (length > max) {
      const extraCount = this.i18n.t('common.toSentence.wordsRemaining', {
        count: length - max
      });
      return [...this.values.slice(0, max), extraCount];
    }
    return this.values;
  }
}, _defineProperty(_class3, "positionalParams", ['values']), _class3), (_descriptor = _applyDecoratedDescriptor(_class2.prototype, "i18n", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "_wordsConnector", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "_wordsConnector"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "_lastWordConnector", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "_lastWordConnector"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "_twoWordsConnector", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "_twoWordsConnector"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "parts", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "parts"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "truncatedValues", [_dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "truncatedValues"), _class2.prototype)), _class2)) || _class);
export { JoinedList as default };